import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Input, Row, Segmented, Select,
  Space, Statistic, Table, Tag, Typography
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined, FundOutlined
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import { formatINR, shortDate } from './accountReportsConstants';
import {
  INV_STATUS_COLOR, INV_STATUS_LABEL, friendlyReportError, formatRupee
} from './reportGroupingHelpers';
import '../accounts.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investment Schedule page (Stage 6B.9G).
//
// Read-only schedule of investments register: principal / current
// value / accrued income / status per investment, grouped by
// investment_class. Backend computes totals + grouping.
// ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '',        label: 'All statuses' },
  { value: 'active',  label: 'Active' },
  { value: 'matured', label: 'Matured' },
  { value: 'sold',    label: 'Sold' },
  { value: 'closed',  label: 'Closed' }
];
const CLASS_OPTIONS = [
  { value: '',        label: 'All classes' },
  { value: 'debt',    label: 'Debt' },
  { value: 'equity',  label: 'Equity' },
  { value: 'hybrid',  label: 'Hybrid' },
  { value: 'deposit', label: 'Deposit (FD/RD)' },
  { value: 'bond',    label: 'Bond / Govt. Securities' },
  { value: 'other',   label: 'Other' }
];

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  const cols = [
    'investmentCode', 'investmentName', 'categoryName', 'investmentClass',
    'institutionName', 'investmentDate', 'maturityDate',
    'principalAmount', 'currentValue', 'accruedIncome',
    'units', 'interestRate', 'status',
    'disposalDate', 'disposalValue', 'gainLossOnDisposal'
  ];
  const lines = [cols.join(',')];
  for (const r of rows) {
    lines.push(cols.map((c) => csvSafe(r[c])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function InvestmentSchedulePage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [asOfDate, setAsOfDate]   = useState(null);
  const [range, setRange]         = useState(null);
  const [status, setStatus]       = useState('');
  const [investmentClass, setInvestmentClass] = useState('');
  const [q, setQ]                 = useState('');
  const [view, setView]           = useState('flat');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (asOfDate) params.asOfDate = asOfDate.format('YYYY-MM-DD');
      if (range && range[0] && range[1]) {
        params.fromDate = range[0].format('YYYY-MM-DD');
        params.toDate   = range[1].format('YYYY-MM-DD');
      }
      if (status)          params.status          = status;
      if (investmentClass) params.investmentClass = investmentClass;
      if (q)               params.q               = q;
      const out = await accountReportsApi.investmentSchedule(params);
      setData(out);
    } catch (e) {
      setLoadError(e);
    } finally { setLoading(false); }
  }, [asOfDate, range, status, investmentClass, q]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows    = data?.rows    || [];
  const groups  = data?.groups  || [];
  const summary = data?.summary || {
    totalPrincipal: 0, totalCurrentValue: 0, totalAccruedIncome: 0,
    totalGainLoss: 0, activeCount: 0, maturedCount: 0,
    closedCount: 0, totalCount: 0
  };
  const meta = data?.meta || {};
  const groupedAvailable = !!meta.groupedViewAvailable && groups.length > 0;

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'investmentCode', key: 'investmentCode', width: 140,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Investment Name', dataIndex: 'investmentName', key: 'investmentName' },
    { title: 'Category', dataIndex: 'categoryName', key: 'categoryName', width: 140,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Class', dataIndex: 'investmentClass', key: 'investmentClass', width: 100,
      render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text> },
    { title: 'Institution', dataIndex: 'institutionName', key: 'institutionName', width: 140,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Invest. Date', dataIndex: 'investmentDate', key: 'investmentDate', width: 110,
      render: (v) => shortDate(v) },
    { title: 'Maturity', dataIndex: 'maturityDate', key: 'maturityDate', width: 110,
      render: (v) => shortDate(v) },
    { title: 'Principal (₹)', dataIndex: 'principalAmount', key: 'principalAmount', width: 120, align: 'right',
      render: (v) => formatINR(v) },
    { title: 'Current (₹)', dataIndex: 'currentValue', key: 'currentValue', width: 120, align: 'right',
      render: (v) => formatINR(v) },
    { title: 'Accrued (₹)', dataIndex: 'accruedIncome', key: 'accruedIncome', width: 120, align: 'right',
      render: (v) => formatINR(v) },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v) => v
        ? <Tag color={INV_STATUS_COLOR[v] || 'default'}>{INV_STATUS_LABEL[v] || v}</Tag>
        : <Text type="secondary">—</Text> }
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', marginBottom: 8 }}>
          <Title level={4} style={{ margin: 0 }}>
            <FundOutlined style={{ marginRight: 8, color: '#7c3aed' }} />
            Investment Schedule
          </Title>
          <Space>
            <Button size="small" icon={<ReloadOutlined />}
              onClick={fetchReport} loading={loading}>Refresh</Button>
            <Button size="small" icon={<PrinterOutlined />}
              onClick={() => window.print()}>Print</Button>
            <Button size="small" icon={<DownloadOutlined />}
              disabled={!rows.length}
              onClick={() => downloadCsv('investment-schedule.csv', rows)}>
              Export CSV
            </Button>
          </Space>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Principal, current value, accrued income and maturity status across the register.
        </div>

        {/* Summary cards */}
        <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="Total Principal (₹)" value={summary.totalPrincipal} precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="Current Value (₹)" value={summary.totalCurrentValue}
                precision={2} valueStyle={{ color: '#0369a1' }} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="Accrued Income (₹)" value={summary.totalAccruedIncome}
                precision={2} valueStyle={{ color: '#1f7a25' }} />
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card size="small">
              <Statistic title="Active" value={summary.activeCount} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="Matured / Closed" value={summary.maturedCount + summary.closedCount} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                Total {summary.totalCount} investment(s)
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            <Space>
              <Text>View:</Text>
              <Segmented size="small" value={view} onChange={setView}
                options={[
                  { label: 'Flat',    value: 'flat' },
                  { label: 'Grouped', value: 'grouped', disabled: !groupedAvailable }
                ]} />
            </Space>
            <Space>
              <Text>As of date:</Text>
              <DatePicker size="small" value={asOfDate} onChange={setAsOfDate}
                allowClear format="YYYY-MM-DD" />
            </Space>
            <Space>
              <Text>Investment date range:</Text>
              <RangePicker size="small" value={range} onChange={setRange}
                allowClear format="YYYY-MM-DD" />
            </Space>
            <Space>
              <Text>Class:</Text>
              <Select size="small" value={investmentClass} onChange={setInvestmentClass}
                style={{ width: 170 }} options={CLASS_OPTIONS} />
            </Space>
            <Space>
              <Text>Status:</Text>
              <Select size="small" value={status} onChange={setStatus}
                style={{ width: 140 }} options={STATUS_OPTIONS} />
            </Space>
            <Space>
              <Text>Search:</Text>
              <Input.Search size="small" allowClear value={q}
                onChange={(e) => setQ(e.target.value)}
                onSearch={(v) => { setQ(v); fetchReport(); }}
                placeholder="Code / name / institution / folio" style={{ width: 260 }} />
            </Space>
          </Space>
        </Card>

        {loadError ? (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load Investment Schedule"
            description={friendlyReportError(loadError)}
            action={<Button size="small" icon={<ReloadOutlined />}
              onClick={fetchReport}>Retry</Button>} />
        ) : null}

        {/* Body */}
        {view === 'grouped' && groupedAvailable ? (
          <>
            {groups.map((g) => (
              <Card key={g.investmentClass || '__unclassified__'}
                size="small"
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                alignItems: 'baseline' }}>
                    <span>
                      <Text strong>{g.investmentClassLabel}</Text>
                      <Text type="secondary" style={{ marginLeft: 6, fontSize: 11.5 }}>
                        {g.investmentCount} investment(s)
                      </Text>
                    </span>
                    <span style={{ fontSize: 12 }}>
                      <Text type="secondary">Principal: </Text>
                      <Text strong>₹ {formatRupee(g.totalPrincipal)}</Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>· Current: </Text>
                      <Text strong>₹ {formatRupee(g.totalCurrentValue)}</Text>
                    </span>
                  </div>
                }
                style={{ marginBottom: 10 }}>
                <Table size="small" rowKey="id" columns={columns}
                  dataSource={g.investments} pagination={false} />
              </Card>
            ))}
          </>
        ) : (
          <Card size="small" style={{ marginBottom: 10 }}>
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={rows}
              pagination={{ pageSize: 50, size: 'small' }}
              locale={{ emptyText:
                <Empty description={loading ? 'Loading…' : 'No investments match the current filters.'} /> }}
            />
          </Card>
        )}

        <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 11.5 }}>
          Basis: {meta.basis || 'investments_register'} ·
          {meta.tablePresent === false
            ? ' Investments module not provisioned in this tenant.'
            : ` ${summary.totalCount} investment(s) in scope.`}
        </div>
      </div>
    </ErpMasterShell>
  );
}
